import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react';

import { useBrowserRingAvailability } from '@apps/web/hooks/use_browser_ring_availability';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { type OrganizationEvent } from '@apps/web/hooks/use_organization_events';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import {
    callIdsMatch,
    canStartBrowserCall,
    createCallLifecycleStore,
    currentSdkCall,
    type IncomingBrowserCall,
    mergeIncomingCallMetadata,
    ownedDevice,
    projectInboundBrowserCallTestState,
} from '@apps/web/lib/browser_call_lifecycle';
import {
    type BrowserVoiceCall,
    type BrowserVoiceDevice,
    loadBrowserVoiceDeviceConstructor,
} from '@apps/web/lib/browser_voice_device_adapter';
import { type InboundBrowserCallTestWindow } from '@apps/web/lib/inbound_browser_call_test_contract';
import {
    createInboundCallTabChannel,
    type InboundCallTabChannel,
} from '@apps/web/lib/inbound_call_tab_channel';
import { getRunEnvironment } from '@apps/web/lib/runtime_config';
import {
    useOrganization,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

export type BrowserCallStatus = 'idle' | 'connecting' | 'active';

export { type IncomingBrowserCall } from '@apps/web/lib/browser_call_lifecycle';

export type StartBrowserCallParams = {
    readonly organizationId: string;
    readonly caregiverId: string;
    readonly caregiverName: string;
};

export type StartEmergencyCallParams = {
    readonly organizationId: string;
    readonly phoneNumberId: string;
    readonly confirmedAddressVersion: number;
    readonly proceedWithUnregisteredAddress: boolean;
};

type BrowserCallRegistrationRequest =
    | {
          readonly kind: 'caregiver';
          readonly caregiverId: string;
      }
    | {
          readonly kind: 'emergency';
          readonly phoneNumberId: string;
          readonly confirmedAddressVersion: number;
          readonly proceedWithUnregisteredAddress: boolean;
      };

type StartRegisteredBrowserCallParams = {
    readonly organizationId: string;
    readonly displayName: string;
    readonly registrationRequest: BrowserCallRegistrationRequest;
};

export type CallContextType = {
    readonly status: BrowserCallStatus;
    readonly caregiverName: string | null;
    readonly startedAtMs: number | null;
    readonly isMuted: boolean;
    readonly incomingCall: IncomingBrowserCall | null;
    readonly answeredElsewhereCall: IncomingBrowserCall | null;
    readonly isBrowserRingEnabled: boolean;
    readonly isBrowserRingReady: boolean;
    readonly isBrowserAvailabilityEnabled: boolean;
    readonly isAvailableForIncomingCalls: boolean;
    readonly isBrowserAvailabilityPublished: boolean;
    readonly setAvailableForIncomingCalls: (available: boolean) => void;
    readonly startCall: (params: StartBrowserCallParams) => Promise<void>;
    readonly startEmergencyCall: (
        params: StartEmergencyCallParams,
    ) => Promise<void>;
    readonly acceptIncomingCall: () => Promise<void>;
    readonly rejectIncomingCall: () => void;
    readonly hangUp: () => void;
    readonly toggleMute: () => void;
};

const CallContext = createContext<CallContextType>({
    status: 'idle',
    caregiverName: null,
    startedAtMs: null,
    isMuted: false,
    incomingCall: null,
    answeredElsewhereCall: null,
    isBrowserRingEnabled: false,
    isBrowserRingReady: false,
    isBrowserAvailabilityEnabled: false,
    isAvailableForIncomingCalls: false,
    isBrowserAvailabilityPublished: false,
    setAvailableForIncomingCalls: () => {},
    startCall: async () => {},
    startEmergencyCall: async () => {},
    acceptIncomingCall: async () => {},
    rejectIncomingCall: () => {},
    hangUp: () => {},
    toggleMute: () => {},
});

export function useCall(): CallContextType {
    return useContext(CallContext);
}

function customParameter(call: BrowserVoiceCall, name: string): string | null {
    const value = call.customParameters.get(name)?.trim();
    return value ? value : null;
}

async function ensureMicrophonePermission(): Promise<boolean> {
    if (!navigator.mediaDevices?.getUserMedia) {
        return false;
    }
    try {
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
        });
        stream.getTracks().forEach((track) => track.stop());
        return true;
    } catch {
        return false;
    }
}

export function CallProvider({ children }: { children: ReactNode }) {
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'call-provider-browser-ring',
    });
    const { data: user } = useUser({ reason: 'call-provider-browser-ring' });
    // Read the flag straight off the organization rather than through
    // useFeatureFlag. This is what the hook resolves to in production anyway —
    // its URL override is gated on a non-production environment — and
    // CallProvider is mounted above <RouterProvider> in main.tsx, where extra
    // dependencies have historically been hazardous (the hook once reached
    // for the router and blanked the app).
    const isBrowserRingEnabled =
        organization?.features?.inbound_browser_ring_enabled === true;
    const isBrowserAvailabilityEnabled =
        organization?.features?.inbound_browser_ring_enabled === true &&
        organization.features.phone_system_browser_availability_enabled ===
            true;
    const callLifecycleStoreRef = useRef<
        ReturnType<typeof createCallLifecycleStore> | undefined
    >(undefined);
    if (!callLifecycleStoreRef.current) {
        callLifecycleStoreRef.current = createCallLifecycleStore();
    }
    const callLifecycleStore = callLifecycleStoreRef.current;
    const callLifecycle = useSyncExternalStore(
        callLifecycleStore.subscribe,
        callLifecycleStore.getSnapshot,
        callLifecycleStore.getSnapshot,
    );
    const transitionCallLifecycle = callLifecycleStore.dispatch;
    const [isBrowserRingReady, setIsBrowserRingReady] = useState(false);
    const persistentDeviceRef = useRef<BrowserVoiceDevice | null>(null);
    const persistentDeviceSetupRef =
        useRef<Promise<BrowserVoiceDevice | null> | null>(null);
    const inboundCallTabChannelRef = useRef<InboundCallTabChannel | null>(null);
    const callAttemptSequenceRef = useRef(0);
    const canReceiveIncomingCall =
        isBrowserRingReady && canStartBrowserCall(callLifecycle);
    const {
        isAvailablePreference: isAvailableForIncomingCalls,
        isPublishedAvailable: isBrowserAvailabilityPublished,
        setAvailablePreference: setAvailableForIncomingCalls,
    } = useBrowserRingAvailability({
        enabled: isBrowserAvailabilityEnabled,
        organizationId: organizationId ?? null,
        userId: user?.id ?? null,
        canReceiveIncomingCall,
    });
    const browserAvailabilityPreferenceRef = useRef(true);
    browserAvailabilityPreferenceRef.current =
        !isBrowserAvailabilityEnabled || isAvailableForIncomingCalls;

    const cleanUpCall = useCallback(
        ({
            expectedSdkCall,
        }: {
            readonly expectedSdkCall?: BrowserVoiceCall;
        } = {}) => {
            const lifecycle = callLifecycleStore.getSnapshot();
            if (
                expectedSdkCall &&
                currentSdkCall(lifecycle) !== expectedSdkCall
            ) {
                return;
            }
            ownedDevice(lifecycle)?.destroy();
            transitionCallLifecycle({ type: 'reset' });
        },
        [callLifecycleStore, transitionCallLifecycle],
    );

    const finishCall = useCallback(
        (sdkCall: BrowserVoiceCall) => {
            const lifecycle = callLifecycleStore.getSnapshot();
            if (currentSdkCall(lifecycle) !== sdkCall) {
                return;
            }
            if (lifecycle.kind === 'answered_elsewhere') {
                transitionCallLifecycle({
                    type: 'detach_answered_elsewhere_leg',
                    sdkCall,
                });
                return;
            }
            if (
                lifecycle.kind === 'active' &&
                lifecycle.inboundCallId !== null
            ) {
                inboundCallTabChannelRef.current?.publish({
                    callId: lifecycle.inboundCallId,
                    state: 'ended',
                });
            }
            cleanUpCall({ expectedSdkCall: sdkCall });
        },
        [callLifecycleStore, cleanUpCall, transitionCallLifecycle],
    );

    const handleIncomingCallEvent = useCallback(
        (event: OrganizationEvent) => {
            if (
                !isBrowserRingEnabled ||
                event.type !== 'incoming_call' ||
                event.data.organization_id !== organizationId
            ) {
                return;
            }
            const eventCall: IncomingBrowserCall = {
                callId: event.data.call_id,
                fromNumberDisplay: event.data.from_number_display,
                callerLabel: event.data.caller_label,
                lineLabel: event.data.line_label,
                ringStartedAt: event.data.ring_started_at,
            };
            transitionCallLifecycle({
                type: 'incoming_metadata',
                caller: eventCall,
            });
        },
        [isBrowserRingEnabled, organizationId, transitionCallLifecycle],
    );
    useListenForOrganizationEvent('incoming_call', handleIncomingCallEvent, {
        organizationId,
    });

    useEffect(() => {
        if (!isBrowserRingEnabled || !organizationId || !user?.id) {
            return;
        }
        const tabChannel = createInboundCallTabChannel({
            organizationId,
            userId: user.id,
            onMessage: (message) => {
                const lifecycle = callLifecycleStore.getSnapshot();
                if (message.state === 'accepted') {
                    if (
                        lifecycle.kind === 'active' &&
                        callIdsMatch(lifecycle.inboundCallId, message.callId)
                    ) {
                        return;
                    }
                    if (
                        lifecycle.kind === 'ringing_inbound' &&
                        callIdsMatch(lifecycle.caller.callId, message.callId)
                    ) {
                        transitionCallLifecycle({
                            type: 'answered_elsewhere',
                            caller: lifecycle.caller,
                            sdkCall: lifecycle.sdkCall,
                        });
                    }
                    return;
                }
                if (
                    lifecycle.kind === 'ringing_inbound' &&
                    callIdsMatch(lifecycle.caller.callId, message.callId)
                ) {
                    lifecycle.sdkCall.reject();
                    cleanUpCall({ expectedSdkCall: lifecycle.sdkCall });
                    return;
                }
                if (
                    lifecycle.kind === 'answered_elsewhere' &&
                    callIdsMatch(lifecycle.caller.callId, message.callId)
                ) {
                    if (lifecycle.sdkCall) {
                        lifecycle.sdkCall.reject();
                        cleanUpCall({
                            expectedSdkCall: lifecycle.sdkCall,
                        });
                    } else {
                        cleanUpCall();
                    }
                }
            },
        });
        inboundCallTabChannelRef.current = tabChannel;
        return () => {
            if (inboundCallTabChannelRef.current === tabChannel) {
                inboundCallTabChannelRef.current = null;
            }
            tabChannel.close();
        };
    }, [
        callLifecycleStore,
        cleanUpCall,
        isBrowserRingEnabled,
        organizationId,
        transitionCallLifecycle,
        user?.id,
    ]);

    useEffect(() => {
        if (!isBrowserRingEnabled || !organizationId || !user?.id) {
            return;
        }

        let cancelled = false;
        let device: BrowserVoiceDevice | null = null;
        const browserRingOrganizationId = organizationId;

        async function fetchVoiceToken(): Promise<string> {
            const { token } = await getApiClient().post({
                url: '/organizations/{organization_id}/click-to-call/voice-token',
                pathParams: {
                    organization_id: browserRingOrganizationId,
                },
            });
            return token;
        }

        async function setUpDevice(): Promise<BrowserVoiceDevice | null> {
            try {
                const [token, DeviceConstructor] = await Promise.all([
                    fetchVoiceToken(),
                    loadBrowserVoiceDeviceConstructor(),
                ]);
                if (cancelled) {
                    return null;
                }

                const newDevice = new DeviceConstructor(token);
                device = newDevice;
                persistentDeviceRef.current = newDevice;

                newDevice.on('registered', () => {
                    if (!cancelled) {
                        setIsBrowserRingReady(newDevice.state === 'registered');
                    }
                });
                newDevice.on('unregistered', () => {
                    if (!cancelled) {
                        setIsBrowserRingReady(newDevice.state === 'registered');
                    }
                });
                newDevice.on('error', (error: unknown) => {
                    if (!cancelled) {
                        console.error('Browser ring device error:', error);
                        setIsBrowserRingReady(false);
                    }
                });
                newDevice.on('incoming', (call) => {
                    if (cancelled) {
                        call.reject();
                        return;
                    }
                    if (!browserAvailabilityPreferenceRef.current) {
                        // Twilio addresses every registered tab for a user with
                        // one identity. The routing lease prevents most calls,
                        // but this local guard makes "do not ring this browser"
                        // hold across another profile's lease and routing races.
                        call.reject();
                        return;
                    }
                    const lifecycle = callLifecycleStore.getSnapshot();
                    if (!canStartBrowserCall(lifecycle)) {
                        // The softphone exposes controls for one call. Keep
                        // the current lifecycle attached to its SDK call.
                        call.reject();
                        return;
                    }
                    const callId = customParameter(call, 'call_id');
                    const immediateCall: IncomingBrowserCall = {
                        callId,
                        fromNumberDisplay: call.parameters.From ?? 'Unknown',
                        callerLabel: customParameter(call, 'caller_label'),
                        lineLabel: customParameter(call, 'line_label'),
                        ringStartedAt: customParameter(call, 'ring_started_at'),
                    };
                    const pendingIncomingCall = lifecycle.pendingIncomingCall;
                    const nextIncomingCall = mergeIncomingCallMetadata(
                        immediateCall,
                        pendingIncomingCall,
                    );
                    const resolvedState = callId
                        ? inboundCallTabChannelRef.current?.getResolvedState(
                              callId,
                          )
                        : undefined;
                    if (resolvedState) {
                        call.reject();
                        if (resolvedState === 'accepted') {
                            transitionCallLifecycle({
                                type: 'answered_elsewhere',
                                caller: nextIncomingCall,
                                sdkCall: null,
                            });
                        }
                        return;
                    }
                    transitionCallLifecycle({
                        type: 'ring_inbound',
                        caller: nextIncomingCall,
                        sdkCall: call,
                    });
                    call.on('accept', () => {
                        const currentLifecycle =
                            callLifecycleStore.getSnapshot();
                        if (
                            currentLifecycle.kind !== 'ringing_inbound' ||
                            currentLifecycle.sdkCall !== call
                        ) {
                            return;
                        }
                        transitionCallLifecycle({
                            type: 'activate_call',
                            sdkCall: call,
                            startedAtMs: Date.now(),
                        });
                        if (currentLifecycle.caller.callId) {
                            inboundCallTabChannelRef.current?.publish({
                                callId: currentLifecycle.caller.callId,
                                state: 'accepted',
                            });
                        }
                    });
                    call.on('cancel', () => {
                        const currentLifecycle =
                            callLifecycleStore.getSnapshot();
                        if (
                            currentLifecycle.kind === 'answered_elsewhere' &&
                            currentLifecycle.sdkCall === call
                        ) {
                            transitionCallLifecycle({
                                type: 'detach_answered_elsewhere_leg',
                                sdkCall: call,
                            });
                            return;
                        }
                        cleanUpCall({ expectedSdkCall: call });
                    });
                    call.on('disconnect', () => {
                        finishCall(call);
                    });
                    call.on('error', (error: unknown) => {
                        console.error('Incoming browser call error:', error);
                        finishCall(call);
                    });
                });
                newDevice.on('tokenWillExpire', () => {
                    void (async () => {
                        try {
                            const token = await fetchVoiceToken();
                            if (
                                !cancelled &&
                                persistentDeviceRef.current === newDevice
                            ) {
                                newDevice.updateToken(token);
                            }
                        } catch (error) {
                            if (!cancelled) {
                                console.error(
                                    'Failed to refresh browser ring token:',
                                    error,
                                );
                            }
                        }
                    })();
                });

                await newDevice.register();
                if (cancelled) {
                    return null;
                }
                setIsBrowserRingReady(newDevice.state === 'registered');
                return newDevice;
            } catch (error) {
                if (!cancelled) {
                    console.error(
                        'Failed to register browser ring device:',
                        error,
                    );
                    setIsBrowserRingReady(false);
                }
                return null;
            }
        }

        const setupPromise = setUpDevice();
        persistentDeviceSetupRef.current = setupPromise;

        return () => {
            cancelled = true;
            if (persistentDeviceSetupRef.current === setupPromise) {
                persistentDeviceSetupRef.current = null;
            }
            if (persistentDeviceRef.current === device) {
                persistentDeviceRef.current = null;
            }
            setIsBrowserRingReady(false);
            const sdkCall = currentSdkCall(callLifecycleStore.getSnapshot());
            if (sdkCall) {
                finishCall(sdkCall);
            } else {
                cleanUpCall();
            }
            if (device) {
                void device.unregister().catch((error: unknown) => {
                    console.error(
                        'Failed to unregister browser ring device:',
                        error,
                    );
                });
                device.destroy();
            }
        };
    }, [
        callLifecycleStore,
        cleanUpCall,
        finishCall,
        isBrowserRingEnabled,
        organizationId,
        transitionCallLifecycle,
        user?.id,
    ]);

    const startRegisteredBrowserCall = useCallback(
        async ({
            organizationId,
            displayName,
            registrationRequest,
        }: StartRegisteredBrowserCallParams) => {
            const lifecycle = callLifecycleStore.getSnapshot();
            if (!canStartBrowserCall(lifecycle)) {
                toast.error('A call is already in progress.', {
                    persistNotification: false,
                });
                return;
            }
            const attemptId = ++callAttemptSequenceRef.current;
            transitionCallLifecycle({
                type: 'start_outbound',
                attemptId,
                caregiverName: displayName,
            });
            const isCancelled = () => {
                const currentLifecycle = callLifecycleStore.getSnapshot();
                return (
                    currentLifecycle.kind !== 'connecting_outbound' ||
                    currentLifecycle.attemptId !== attemptId
                );
            };
            try {
                if (!(await ensureMicrophonePermission())) {
                    throw new Error('Microphone unavailable');
                }
                if (isCancelled()) {
                    return;
                }
                const apiClient = getApiClient();
                function createBrowserCall() {
                    if (registrationRequest.kind === 'emergency') {
                        return apiClient.post({
                            url: '/organizations/{organization_id}/phone-system/emergency-calling/calls',
                            pathParams: {
                                organization_id: organizationId,
                            },
                            body: {
                                phone_number_id:
                                    registrationRequest.phoneNumberId,
                                confirmed_address_version:
                                    registrationRequest.confirmedAddressVersion,
                                proceed_with_unregistered_address:
                                    registrationRequest.proceedWithUnregisteredAddress,
                            },
                        });
                    }
                    return apiClient.post({
                        url: '/organizations/{organization_id}/click-to-call/calls',
                        pathParams: { organization_id: organizationId },
                        body: {
                            caregiver_id: registrationRequest.caregiverId,
                            call_mode: 'browser',
                        },
                    });
                }

                let device: BrowserVoiceDevice;
                let registration: Awaited<ReturnType<typeof createBrowserCall>>;
                if (isBrowserRingEnabled) {
                    const persistentDevice =
                        await persistentDeviceSetupRef.current;
                    if (!persistentDevice) {
                        throw new Error('Browser ring device is not ready');
                    }
                    if (isCancelled()) {
                        return;
                    }
                    device = persistentDevice;
                    registration = await createBrowserCall();
                } else {
                    const [{ token }, nextRegistration, DeviceConstructor] =
                        await Promise.all([
                            apiClient.post({
                                url: '/organizations/{organization_id}/click-to-call/voice-token',
                                pathParams: {
                                    organization_id: organizationId,
                                },
                            }),
                            createBrowserCall(),
                            loadBrowserVoiceDeviceConstructor(),
                        ]);
                    if (isCancelled()) {
                        return;
                    }
                    device = new DeviceConstructor(token);
                    transitionCallLifecycle({
                        type: 'attach_outbound_device',
                        attemptId,
                        device,
                    });
                    registration = nextRegistration;
                }
                if (isCancelled()) {
                    return;
                }
                const call = await device.connect({
                    params: {
                        voice_call_id: registration.voice_call_id,
                        nonce: registration.browser_call_nonce ?? '',
                    },
                });
                if (isCancelled()) {
                    // A newer lifecycle owns the panel. Tear down only this
                    // stale SDK call and leave the current lifecycle intact.
                    call.disconnect();
                    return;
                }
                transitionCallLifecycle({
                    type: 'attach_outbound_call',
                    attemptId,
                    sdkCall: call,
                });
                call.on('accept', () => {
                    transitionCallLifecycle({
                        type: 'activate_call',
                        sdkCall: call,
                        startedAtMs: Date.now(),
                    });
                });
                call.on('disconnect', () => {
                    finishCall(call);
                });
                call.on('error', (error: unknown) => {
                    if (
                        currentSdkCall(callLifecycleStore.getSnapshot()) !==
                        call
                    ) {
                        return;
                    }
                    console.error('Browser call error:', error);
                    toast.error('Call failed', {
                        description: 'Please try again.',
                        persistNotification: false,
                    });
                    finishCall(call);
                });
            } catch (error) {
                if (isCancelled()) {
                    // The user hung up while setup was in flight; the
                    // cancelling cleanup already reset state.
                    return;
                }
                console.error('Failed to start browser call:', error);
                toast.error('Failed to start the call', {
                    description:
                        'Check that your microphone is allowed and try again.',
                    persistNotification: false,
                });
                cleanUpCall();
            }
        },
        [
            callLifecycleStore,
            cleanUpCall,
            finishCall,
            isBrowserRingEnabled,
            transitionCallLifecycle,
        ],
    );

    const startCall = useCallback(
        async ({
            organizationId,
            caregiverId,
            caregiverName,
        }: StartBrowserCallParams) => {
            await startRegisteredBrowserCall({
                organizationId,
                displayName: caregiverName,
                registrationRequest: {
                    kind: 'caregiver',
                    caregiverId,
                },
            });
        },
        [startRegisteredBrowserCall],
    );

    const startEmergencyCall = useCallback(
        async ({
            organizationId,
            phoneNumberId,
            confirmedAddressVersion,
            proceedWithUnregisteredAddress,
        }: StartEmergencyCallParams) => {
            await startRegisteredBrowserCall({
                organizationId,
                displayName: 'Emergency services',
                registrationRequest: {
                    kind: 'emergency',
                    phoneNumberId,
                    confirmedAddressVersion,
                    proceedWithUnregisteredAddress,
                },
            });
        },
        [startRegisteredBrowserCall],
    );

    const acceptIncomingCall = useCallback(async () => {
        const lifecycle = callLifecycleStore.getSnapshot();
        if (lifecycle.kind !== 'ringing_inbound') {
            return;
        }
        if (!(await ensureMicrophonePermission())) {
            toast.error('Microphone access is required to answer the call.', {
                persistNotification: false,
            });
            return;
        }
        const currentLifecycle = callLifecycleStore.getSnapshot();
        if (
            currentLifecycle.kind !== 'ringing_inbound' ||
            currentLifecycle.sdkCall !== lifecycle.sdkCall
        ) {
            return;
        }
        lifecycle.sdkCall.accept();
    }, [callLifecycleStore]);

    const rejectIncomingCall = useCallback(() => {
        const lifecycle = callLifecycleStore.getSnapshot();
        if (lifecycle.kind !== 'ringing_inbound') {
            return;
        }
        if (lifecycle.caller.callId) {
            inboundCallTabChannelRef.current?.publish({
                callId: lifecycle.caller.callId,
                state: 'declined',
            });
        }
        lifecycle.sdkCall.reject();
        cleanUpCall({ expectedSdkCall: lifecycle.sdkCall });
    }, [callLifecycleStore, cleanUpCall]);

    const hangUp = useCallback(() => {
        const sdkCall = currentSdkCall(callLifecycleStore.getSnapshot());
        if (!sdkCall) {
            cleanUpCall();
            return;
        }
        sdkCall.disconnect();
        // The disconnect event normally cleans up; do it eagerly too in case
        // the call never fully connected.
        finishCall(sdkCall);
    }, [callLifecycleStore, cleanUpCall, finishCall]);

    const toggleMute = useCallback(() => {
        const lifecycle = callLifecycleStore.getSnapshot();
        if (lifecycle.kind !== 'active') {
            return;
        }
        const nextMuted = !lifecycle.sdkCall.isMuted();
        lifecycle.sdkCall.mute(nextMuted);
        transitionCallLifecycle({
            type: 'set_muted',
            isMuted: nextMuted,
        });
    }, [callLifecycleStore, transitionCallLifecycle]);

    const status: BrowserCallStatus =
        callLifecycle.kind === 'connecting_outbound'
            ? 'connecting'
            : callLifecycle.kind === 'active'
              ? 'active'
              : 'idle';
    const caregiverName =
        callLifecycle.kind === 'connecting_outbound'
            ? callLifecycle.caregiverName
            : callLifecycle.kind === 'active'
              ? callLifecycle.displayName
              : null;
    const startedAtMs =
        callLifecycle.kind === 'active' ? callLifecycle.startedAtMs : null;
    const isMuted =
        callLifecycle.kind === 'active' ? callLifecycle.isMuted : false;
    const incomingCall =
        callLifecycle.kind === 'ringing_inbound' ? callLifecycle.caller : null;
    const answeredElsewhereCall =
        callLifecycle.kind === 'answered_elsewhere'
            ? callLifecycle.caller
            : null;

    useEffect(() => {
        const testWindow = window as InboundBrowserCallTestWindow;
        if (
            getRunEnvironment() !== 'development' ||
            !isBrowserRingEnabled ||
            testWindow.__phoebeEnableInboundBrowserCallTest__ !== true
        ) {
            return;
        }
        const callProjection =
            projectInboundBrowserCallTestState(callLifecycle);
        const controller = {
            getSnapshot: () => ({
                deviceReady: isBrowserRingReady,
                availabilityEnabled: isBrowserAvailabilityEnabled,
                availablePreference: isAvailableForIncomingCalls,
                availabilityPublished: isBrowserAvailabilityPublished,
                ...callProjection,
            }),
            setAvailable: setAvailableForIncomingCalls,
            accept: acceptIncomingCall,
            hangUp,
        };
        testWindow.__phoebeInboundBrowserCallTest__ = controller;
        return () => {
            if (testWindow.__phoebeInboundBrowserCallTest__ === controller) {
                delete testWindow.__phoebeInboundBrowserCallTest__;
            }
        };
    }, [
        acceptIncomingCall,
        callLifecycle,
        hangUp,
        isAvailableForIncomingCalls,
        isBrowserAvailabilityEnabled,
        isBrowserAvailabilityPublished,
        isBrowserRingEnabled,
        isBrowserRingReady,
        setAvailableForIncomingCalls,
    ]);

    const value = useMemo(
        () => ({
            status,
            caregiverName,
            startedAtMs,
            isMuted,
            incomingCall,
            answeredElsewhereCall,
            isBrowserRingEnabled,
            isBrowserRingReady,
            isBrowserAvailabilityEnabled,
            isAvailableForIncomingCalls,
            isBrowserAvailabilityPublished,
            setAvailableForIncomingCalls,
            startCall,
            startEmergencyCall,
            acceptIncomingCall,
            rejectIncomingCall,
            hangUp,
            toggleMute,
        }),
        [
            answeredElsewhereCall,
            caregiverName,
            startedAtMs,
            incomingCall,
            isBrowserRingEnabled,
            isBrowserRingReady,
            isBrowserAvailabilityEnabled,
            isAvailableForIncomingCalls,
            isBrowserAvailabilityPublished,
            isMuted,
            status,
            startCall,
            startEmergencyCall,
            setAvailableForIncomingCalls,
            acceptIncomingCall,
            rejectIncomingCall,
            hangUp,
            toggleMute,
        ],
    );

    return (
        <CallContext.Provider value={value}>{children}</CallContext.Provider>
    );
}
